<template>
  <Loading v-if="loading" />
  <Modal @close="closeModal">
    <div class="reserveLocker">
      <div class="reserveLocker__header">
        <span class="title">
          {{
            isReservations
              ? $t('newBookingPharmacyPickup')
              : $t('reserveLocker')
          }}
        </span>
        <img
          src="@/assets/icons/CloseModal.svg"
          alt="CloseModal"
          @click="closeModal"
        />
      </div>
      <div class="reserveLocker__body">
        <FormKit
          id="createReservation-form"
          v-model="formData"
          type="form"
          :actions="false"
          :config="validationRules"
          @submit="submitHandler"
        >
          <Input
            type="tel"
            name="phoneNumber"
            :label="$t('phoneNumber')"
            :validation="[['required'], ['matches', /^\+[0-9]{10,14}$/]]"
            validation-visibility="dirty"
            :validation-messages="{
              matches: $t('validation.phoneNumber'),
              required: $t('validation.required')
            }"
          />
          <Checkbox
            :class="{
              disabledCheckbox: pharmacy.strongAuthentication === 'off'
            }"
            name="is_drug"
            :value="formData.is_drug"
            :title="$t('isDrug')"
            :disabled="pharmacy.strongAuthentication === 'off'"
            @change="formData.is_drug = !formData.is_drug"
          />
          <div v-if="formData.is_drug" class="block__ssn">
            <label class="label-ssn" for="ssn">
              {{ $t('ssn') }}
            </label>
            <Input v-model="formData.ssn" type="text" name="ssn" />
          </div>
        </FormKit>
      </div>
      <div class="reserveLocker__buttons">
        <Button variant="filled" :title="$t('reserve')" @click="submitForm" />
        <Button
          variant="notBordered"
          :title="$t('cancel')"
          @click="closeModal"
        />
      </div>
    </div>
  </Modal>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';

import router from '@/router';
import { DataCreatePharmacyReservation } from '@/store/modules/pharmacy/types';

import { goToPickupBox } from '@/utils/goToPickupBox';

@Options({
  name: 'ReserveLocker',
  props: [
    'showModalReserveLocker',
    'selectedLockers',
    'activePickupBox',
    'loadPickupBoxes'
  ],
  emits: ['closeModal'],
  components: {
    Loading
  },
  data() {
    return {
      formData: {
        deliveryId: '',
        phoneNumber: '',
        is_drug: false,
        additionalDeliveryIds: [],
        ssn: ''
      },
      payload: null,
      validationRules: {},
      loading: false
    };
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('pharmacy', ['pharmacy']),
    isReservations() {
      return this.$route.name === 'Reservations';
    }
  },
  methods: {
    ...mapActions('pharmacy', [
      'createReservation',
      'createPharmacyReservation',
      'getPharmacy',
      'getPickupBoxesByPharmacy'
    ]),
    async submitHandler(data: DataCreatePharmacyReservation) {
      if (this.selectedLockers) {
        if (typeof this.selectedLockers === 'string') {
          this.payload = {
            ...data,
            deliveryId: this.formData.deliveryId
              .replace(/\s+/g, '')
              .toLowerCase(),
            userId: this.user.id,
            lockerId: this.selectedLockers.split(' ')
          };
        } else {
          this.payload = {
            ...data,
            deliveryId: this.formData.deliveryId
              .replace(/\s+/g, '')
              .toLowerCase(),
            userId: this.user.id,
            lockerId: [...this.selectedLockers]
          };
        }
      }

      if (this.isReservations) {
        this.payload = {
          ...data,
          deliveryId: this.formData.deliveryId
            .replace(/\s+/g, '')
            .toLowerCase(),
          userId: this.user.id
        };
      }

      this.loading = true;

      if (this.payload.deliveryId === '') {
        this.payload = { ...this.payload, deliveryId: undefined };
      }

      const { id } = this.$route.params;

      if (this.isReservations) {
        await this.createPharmacyReservation({
          data: this.payload,
          pharmacyId: this.user.pharmacyId
        });

        router.push({ name: 'Reservations' });
        this.loadPickupBoxes();
      } else {
        await this.createReservation({
          data: this.payload,
          boxId: this.activePickupBox.id
        });

        goToPickupBox(id, this.activePickupBox.id);

        await this.getPickupBoxesByPharmacy(id);
      }

      this.$formkit.reset('createReservation-form');
      this.$emit('closeModal');
      this.loading = false;
    },
    submitForm() {
      this.$formkit.submit('createReservation-form');
    },
    closeModal() {
      this.$emit('closeModal');
      this.$formkit.reset('createReservation-form');
    }
  }
})
export default class ReserveLocker extends Vue {}
</script>

<style lang="scss" scoped>
.reserveLocker {
  width: 460px;
  height: 100%;
  .reserveLocker__header {
    display: flex;
    justify-content: space-between;
    padding: 24px 24px 0 24px;
    .title {
      font-family: 'Ubuntu-Medium';
      font-size: $eighteenSize;
      line-height: 14px;
    }
    img {
      cursor: pointer;
    }
  }
  .reserveLocker__body {
    padding: 24px;
    .formkit-form {
      .disabledCheckbox {
        display: none;
      }
      .block__ssn {
        position: relative;
        width: 412px;
        height: 48px;
        margin-top: 24px;
        label {
          position: absolute;
          font-family: 'Ubuntu-Regular';
          top: -6px;
          left: 15px;
          padding: 0 5px;
          background: $white;
          color: $label;
          font-weight: 400;
          font-size: $twelveSize;
          line-height: $fourteenSize;
          z-index: 1;
        }
      }
    }
  }
  .reserveLocker__buttons {
    padding: 0 24px 24px 24px;
  }
}
</style>
